/**
 * Máquina de estados do post.
 * Fonte: docs/05_Fluxo_Aprovacao_e_Postagem.md §2.1
 *
 * Qualquer transição fora desta tabela deve ser rejeitada com 409 INVALID_TRANSITION.
 */

import type { ActorType, PostStatus } from "./types.js";

export interface Transition {
  from: PostStatus;
  to: PostStatus;
  /** Quem pode disparar. "system" é o cron. */
  actors: ActorType[];
}

export const TRANSITIONS: readonly Transition[] = [
  { from: "draft", to: "pending_approval", actors: ["user"] },
  { from: "draft", to: "archived", actors: ["user"] },

  { from: "pending_approval", to: "approved", actors: ["client", "user"] },
  { from: "pending_approval", to: "revision_requested", actors: ["client", "user"] },
  { from: "pending_approval", to: "archived", actors: ["user"] },

  { from: "revision_requested", to: "pending_approval", actors: ["user"] },
  { from: "revision_requested", to: "archived", actors: ["user"] },

  { from: "approved", to: "publishing", actors: ["system"] },
  { from: "approved", to: "needs_reschedule", actors: ["system"] },
  { from: "approved", to: "pending_approval", actors: ["user"] },

  { from: "needs_reschedule", to: "approved", actors: ["user"] },

  { from: "publishing", to: "published", actors: ["system"] },
  { from: "publishing", to: "failed", actors: ["system"] },
  // recuperação de post preso (docs/09 §3.5)
  { from: "publishing", to: "approved", actors: ["system"] },

  { from: "failed", to: "publishing", actors: ["system"] },
  { from: "failed", to: "approved", actors: ["system"] },
  { from: "failed", to: "needs_reschedule", actors: ["user"] },

  { from: "published", to: "archived", actors: ["user"] },
] as const;

export function canTransition(
  from: PostStatus,
  to: PostStatus,
  actor: ActorType,
): boolean {
  return TRANSITIONS.some(
    (t) => t.from === from && t.to === to && t.actors.includes(actor),
  );
}

/** Estados que a cliente nunca vê como tais — docs/02 §6. */
const CLIENT_LABEL: Record<PostStatus, string | null> = {
  draft: null,
  pending_approval: "Em análise",
  revision_requested: "Revisão pedida",
  approved: "Aprovado",
  // Problema técnico é assunto entre o sistema e a Lara: para a cliente segue "Aprovado".
  needs_reschedule: "Aprovado",
  publishing: "Aprovado",
  published: "Publicado",
  failed: "Aprovado",
  archived: null,
};

const ADMIN_LABEL: Record<PostStatus, string> = {
  draft: "Rascunho",
  pending_approval: "Em aprovação",
  revision_requested: "Revisão pedida",
  approved: "Aprovado",
  needs_reschedule: "Reagendar",
  publishing: "Publicando",
  published: "Publicado",
  failed: "Falhou",
  archived: "Arquivado",
};

export function statusLabel(status: PostStatus, audience: "admin" | "client"): string | null {
  return audience === "admin" ? ADMIN_LABEL[status] : CLIENT_LABEL[status];
}
